import { useEffect, useState } from "react";
import Hero from "../components/Hero";
import ServicesSection from "../components/ServicesSection";
import TestimonialSlider from "../components/TestimonialSlider";
import GalleryLightbox from "../components/GalleryLightbox";
import CtaBanner from "../components/CtaBanner";
import { PROJECTS_GALLERY } from "../data/siteData";

export default function HomePage() {
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    // Set document head title and meta description
    document.title = "Kærby Tømrer & Snedkerforretning ApS – Tømrer, Tilbygning, Randers, Hadsund, Mariager";

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Kærby Tømrer & Snedkerforretning ApS tilbyder professionelt tømrer- og snedkerarbejde, tilbygninger, nybyggeri, tagudskiftning, døre & vinduer og isolering i Randers, Hadsund og Mariager."
      );
    }

    // Fetch page data from CMS
    fetch("https://api.dev.auxo.dk/public/v2/content/pages?page_slug=forside")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          setCmsPage(data.items[0]);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-0 bg-stone-100 text-stone-900">
      
      {/* Hero Banner */}
      <Hero />

      {/* ALTERNATING FEATURE GRID (DIAGONAL STAGGERED LAYOUT) */}
      <section className="bg-stone-50 text-stone-900 border-t border-b border-stone-200 py-12 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            
            {/* LEFT COLUMN: Heading + Body Text + Ghost CTA + Landscape Image */}
            <div className="flex flex-col space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-stone-900 leading-tight">
                Hvad kan vi hjælpe dig med?
              </h2>

              <div
                className="prose prose-stone text-stone-700 space-y-4 leading-relaxed text-sm sm:text-base font-normal"
                dangerouslySetInnerHTML={{
                  __html: cmsPage?.content || `
                    <p>Hos Kærby Tømrer- & Snedkerforretning ApS leverer vi professionelt håndværk af højeste kvalitet. Med mange års erfaring inden for tømrer- og snedkerfaget sikrer vi, at dine byggeprojekter udføres til tiden og efter alle kunstens regler.</p>
                    <p>Uanset om du skal have bygget ny træterrasse, udskiftet vinduer eller igangsat en større tilbygning, er vi din pålidelige partner i Randers, Hadsund, Mariager og omegn. Vi tegner jeres drømmebolig og søger de nødvendige tilladelser.</p>
                  `
                }}
              />

              <div className="pt-2">
                <a
                  href="/om-os"
                  className="inline-flex items-center justify-center gap-2.5 border-2 border-stone-900 hover:bg-stone-900 hover:text-white text-stone-900 font-extrabold uppercase tracking-wider text-xs px-6 py-3.5 rounded-sm transition-all shadow-xs"
                >
                  <span>Læs mere om virksomheden</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>

              {/* Wide Landscape Image */}
              <div className="pt-4 sm:pt-6">
                <div className="relative rounded-sm overflow-hidden shadow-md border border-stone-200 aspect-[16/10] group">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/c825601b-4884-4adf-bb6b-b4af6f529bc2/cta-nybyg.jpg"
                    alt="Kærby Tømrer Nybyggeri"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN (Staggered Vertical Offset): Portrait Image + Heading + Body Text + Checklist + Ghost CTA */}
            <div className="flex flex-col space-y-6 lg:pt-16">
              
              {/* Portrait Image at Top */}
              <div className="relative rounded-sm overflow-hidden shadow-md border border-stone-200 aspect-[16/10] group">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/c825601b-4884-4adf-bb6b-b4af6f529bc2/1-syd-facade-efter-640x480-c.jpg"
                  alt="Kærby Tømrer Facade"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>

              <div className="pt-2 space-y-4">
                <h3 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-stone-900 leading-tight">
                  Vores arbejdskompetencer
                </h3>

                <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                  Vi løser en bred vifte af opgaver for private og erhverv i Randers, Mariager, Hadsund og omegn:
                </p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs sm:text-sm font-semibold text-stone-800">
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Tilbygning & Ombygning</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Nybyggeri</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Renovering & Modernisering</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Tagarbejde & Tagudskiftning</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Døre & Vinduer</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Træterrasser & Uderum</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Snedkerarbejde & Inventar</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-[#E07A38] text-sm shrink-0" aria-hidden="true" />
                    <span>Isolering & Termografi</span>
                  </li>
                </ul>

                <div className="pt-4">
                  <a
                    href="/ydelser"
                    className="inline-flex items-center justify-center gap-2.5 border-2 border-stone-900 hover:bg-stone-900 hover:text-white text-stone-900 font-extrabold uppercase tracking-wider text-xs px-6 py-3.5 rounded-sm transition-all shadow-xs"
                  >
                    <span>Læs mere om vores arbejdsområder</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* Services Grid Component */}
      <ServicesSection />

      {/* Customer Testimonials Slider */}
      <TestimonialSlider />

      {/* Cta Banner */}
      <CtaBanner />

    </div>
  );
}
